<template>
  <div class="homepage v2">
    <header class="homepage-header">
      <h1 class="homepage-logo" :alt="$t('sd.slogan')">
        <img v-if="isZh" src="//smb.cdn/materialMini/brand/sd-logo.svg" :alt="$t('sd.slogan')" />

        <img v-else src="//smb.cdn/materialMini/brand/sd-cloud-logo.svg" :alt="$t('sd.slogan')" />

        <!-- <img v-if="isZh" class="beta" :src="isZh ? getImage('iconBetaCn') : getImage('iconBetaIntl')" /> -->
      </h1>

      <nav class="homepage-nav">
        <router-link
          to="/home"
          :class="$route.path === '/home' || $route.path === '/home/' ? 'active' : ''"
          :title="$t('home.schemes')"
          >{{ $t('home.schemes') }}</router-link
        >
        <router-link
          to="/home/product"
          :class="$route.path === '/home/product' ? 'active' : ''"
          :title="$t('home.products')"
          >{{ $t('home.products') }}</router-link
        >
      </nav>

      <aside class="homepage-topbar">
        <a-dropdown overlayClassName="user-dropdown">
          <div class="user-meta">
            <img :src="getImage('defaultAvatar')" :alt="userInfo.userName" />
            <strong>{{ userInfo.userName }}</strong>
          </div>

          <template #overlay>
            <a-menu>
              <a-menu-item>{{ userInfo.userName }}</a-menu-item>
              <a-menu-item @click="onLogout">{{ $t('common.exit') }}</a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
      </aside>
    </header>

    <main class="homepage-body">
      <keep-alive>
        <router-view></router-view>
      </keep-alive>
    </main>
  </div>
</template>

<script>
import { requestOpen } from '@/utils';
import { Modal } from 'ant-design-vue';
import { mapGetters, mapState } from 'vuex';

/**
 * 新版首页
 *
 * **[需求设计文档](https://lysora.feishu.cn/wiki/Va1XwSOUqiea0nkHe9ScxTjhntF)**
 * @author 陈旭
 * @see https://lysora.feishu.cn/wiki/Va1XwSOUqiea0nkHe9ScxTjhntF
 *
 * **[设计稿](https://www.figma.com/design/GVuLWmipuPRy9eLkyGL5Aj/%E5%94%AE%E5%89%8D%E4%BA%91%E5%9C%B0%E5%8B%982.0%E4%BD%93%E9%AA%8C%E4%BC%98%E5%8C%96%E4%B8%93%E9%A1%B9?node-id=5164-434055&node-type=frame&t=oiFN4vu63SrmQyP3-0)**
 * @author 梁可
 * @see https://www.figma.com/design/GVuLWmipuPRy9eLkyGL5Aj/%E5%94%AE%E5%89%8D%E4%BA%91%E5%9C%B0%E5%8B%982.0%E4%BD%93%E9%AA%8C%E4%BC%98%E5%8C%96%E4%B8%93%E9%A1%B9?node-id=5164-434055&node-type=frame&t=oiFN4vu63SrmQyP3-0
 */
export default {
  name: 'NewHomepage',

  data() {
    return {};
  },

  computed: {
    ...mapState('setting', ['multiPage', 'animate']),
    ...mapState('User', ['userInfo']),
    ...mapGetters(['language', 'isZh'])
  },

  mounted() {
    /**
     * 埋点 --- 进入云地勘首页
     */
    window.sensorsEvent('designHomeV', {
      sd_event_alias: '进入云地勘首页',
      from: this.$route.query.from || 'design_site'
    });

    document.body.classList.add('v2');
  },

  beforeRouteLeave(_to, _form, next) {
    Modal.destroyAll();
    next();
  },

  destroyed() {
    document.body.classList.remove('v2');
  },

  methods: {
    /**
     * 回退到旧版
     */
    onRollback(event) {
      event?.preventDefault();

      window.localStorage.setItem('SD_BOM_DESIGN_USER_VERSION', '1');
      this.$router.push('/home');
    },

    async onLogout() {
      await new Promise((resolve, reject) => {
        this.$confirm({
          title: this.$t('aiWire.verticalTip'),
          icon: () => {
            return <a-icon type="exclamation-circle" theme="filled" class="color-theme" />;
          },
          centered: true,
          content: () => <div>{this.$t('home.userExitTip')}</div>,
          okText: this.$t('common.ok'),
          okType: 'danger',
          cancelText: this.$t('common.cancel'),
          onOk: resolve,
          onCancel: () =>
            reject({
              code: 'AbortError',
              msg: 'The user aborted a request!'
            })
        });
      });

      window?.sensors?.logout();

      const returnURL = new URL(window.location.href);
      returnURL.hash = `v2`;
      const url = new URL('/webproxy/sso/logout', location.href);
      url.searchParams.set('back', returnURL.href);
      window.location.href = url.href;
    }
  }
};
</script>

<style lang="less" scoped>
.homepage {
  --homepage-header-height: 56px;
  --homepage-logo-height: 20px;

  display: grid;
  grid-template-rows: var(--homepage-header-height) 1fr;
  row-gap: var(--spacing-s);
  min-width: 1200px;
  height: 100%;
  background-color: var(--bg_page);
  overflow-y: scroll;
  overscroll-behavior: contain;
  scroll-behavior: smooth;
  scroll-padding-top: var(--homepage-header-height);
  padding-bottom: 50px;
  &:not(:lang(zh)) {
    --homepage-logo-height: 26px;
  }
}

.homepage-header {
  position: sticky;
  top: 0;
  z-index: 99;
  display: grid;
  grid-template-columns: max-content max-content 1fr;
  column-gap: var(--spacing-s);
  align-items: center;
  padding-left: var(--spacing-xl);
  padding-right: var(--spacing-xl);
  border-bottom: 1px solid var(--gray_3);
  background: var(--bg_page);
  background: color-mix(in srgb, var(--bg_page) 85%, transparent);
  backdrop-filter: var(--backdrop-filter);
}

.homepage-logo {
  display: flex;
  align-items: center;
  // width: 128px;
  height: 100%;
  margin: 0;
  padding: 0;
  overflow: hidden;

  > img {
    display: block;
    width: auto;
    height: var(--homepage-logo-height);
    object-fit: contain;

    &.beta {
      height: 18px;
      margin-left: var(--spacing-xs);
    }
  }
}

.homepage-nav {
  display: flex;
  height: 100%;

  > a {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding-left: var(--spacing-xl);
    padding-right: var(--spacing-xl);
    color: var(--text_title);
    font-size: 14px;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      right: 0;
      bottom: 12px;
      width: 16px;
      margin-left: auto;
      margin-right: auto;
      border-radius: var(--radius-xxs);
      border-bottom: 4px solid currentcolor;
      opacity: 0;
      transform: scaleX(0);
    }

    &:hover,
    &:active {
      color: var(--theme_color);
    }

    &.active {
      color: var(--theme_color);
      font-weight: bold;

      &::after {
        transition: opacity 0.2s, transform 0.2s;
        opacity: 1;
        transform: scaleX(1);
      }
    }
  }
}

.homepage-topbar {
  display: grid;
  grid-auto-flow: column;
  column-gap: var(--spacing-s);
  justify-self: end;
  align-items: center;
}

.rollback-button {
  display: grid;
  grid-auto-flow: column;
  column-gap: var(--spacing-xxs);
  justify-content: center;
  align-items: center;
  min-width: 84px;
  height: 30px;
  padding-left: var(--spacing-xs);
  padding-right: var(--spacing-xs);
  border: 1px solid var(--theme_focus);
  border-radius: var(--radius-xs);
  background-color: var(--theme_light);
  font-weight: 500;
  color: var(--theme_color);

  &:hover,
  &:active {
    border-color: currentcolor;
  }

  &:active {
    transform: translateY(1px);
  }

  > i {
    transform: translateY(-1px);
    font-weight: normal;
  }
}

.app-download {
  width: 348px;

  > main {
    display: grid;
    grid-template-columns: 160px 1fr;
    column-gap: var(--spacing-l);
    align-items: center;
    padding: var(--spacing-xs);
    border-radius: var(--radius);
    background-color: var(--onFill_opacity4);

    > img {
      width: 160px;
      height: 160px;
      object-fit: contain;
    }

    > p {
      margin: 0;
      font-size: 16px;
      color: var(--theme_color);
      font-weight: bolder;
    }
  }

  > aside {
    margin-top: var(--spacing-xs);
    padding-top: var(--spacing-xs);
    border-top: 1px solid var(--border_base8);
  }
}

.user-meta {
  display: grid;
  grid-template-columns: 32px max-content 16px;
  column-gap: var(--spacing-s);
  align-items: center;
  cursor: pointer;
  color: var(--text_title);

  // &::before {
  //   content: '';
  //   height: 20px;
  //   border-left: 1px solid var(--gray_7);
  // }

  &::after {
    content: '';
    width: 16px;
    height: 16px;
    background-color: var(--gray_9);
    mask: var(--arrow-fill) center/auto 100% no-repeat;
    transition: transform 0.2s;
  }

  // &.ant-dropdown-open::after,
  // &:hover::after {
  //   transform: rotate(-180deg);
  // }

  > img {
    width: 32px;
    height: 32px;
    object-fit: cover;
  }

  > strong {
    max-width: 150px;
    margin-left: calc(var(--spacing-xxs) * -1);
    margin-right: calc(var(--spacing-xxs) * -1);
    font-weight: normal;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
}

// .homepage-body {
//   // height: 1900px;
// }
</style>
